import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap } from 'rxjs/operators';

import { MatchComponentApi } from './rest/match-component.api';
import {
  IMatchComponent,
  IMatchComponentsResponse,
  IMatchComponentWithImage,
} from '../../../shared/interfaces/match-component.interface';
import { UserPropertyService } from './user-property.service';
import { ICurrentSelectedProperty } from './user.service';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { DEBOUNCE_TIME } from '../shared/constants';
import { $matchComponent } from '../../../shared/constants/match-component';
import { IExistsResponse } from '../../../shared/interfaces/common.interface';
import { InstrumentationApi } from './rest/instrumentation.api';

@Injectable({
  providedIn: 'root',
})
export class MatchComponentService {
  public constructor(
    private matchComponentApi: MatchComponentApi,
    private userPropertyService: UserPropertyService,
    private instrumentationApi: InstrumentationApi,
  ) {}

  public get(workspaceId: string, digitalPropertyId: string, componentId: string): Observable<IMatchComponentWithImage> {
    return this.matchComponentApi.getMatchComponent(workspaceId, digitalPropertyId, componentId);
  }

  public getWithEngagementMetrics(
    workspaceId: string,
    digitalPropertyId: string,
    componentId: string,
  ): Observable<IMatchComponentWithImage> {
    return this.matchComponentApi.getMatchComponentWithEngagementMetrics(workspaceId, digitalPropertyId, componentId);
  }

  public getAll(
    workspaceId: string,
    digitalPropertyId: string,
    page: number = 0,
    limit: number = 0,
    sortBy?: string,
    direction?: string,
    searchTerm?: string,
  ): Observable<IMatchComponentsResponse> {
    return this.matchComponentApi.getMatchComponents(workspaceId, digitalPropertyId, page, limit, sortBy, direction, searchTerm);
  }

  public getImpactful(
    workspaceId: string,
    digitalPropertyId: string,
    page: number,
    limit: number,
  ): Observable<IMatchComponentsResponse> {
    return this.matchComponentApi.getImpactfulMatchComponents(workspaceId, digitalPropertyId, page, limit);
  }

  public createMatchComponent(workspaceId: string, dpId: string, component: FormData): Observable<IMatchComponent> {
    return this.matchComponentApi
      .createMatchComponent(workspaceId, dpId, component)
      .pipe(mergeMap((result: IMatchComponent) => this.instrumentComponentSaved(result, workspaceId, dpId)));
  }

  public updateMatchComponent(
    workspaceId: string,
    dpId: string,
    componentId: string,
    updateComponent: FormData,
  ): Observable<IMatchComponentWithImage> {
    return this.matchComponentApi
      .updateMatchComponent(workspaceId, dpId, componentId, updateComponent)
      .pipe(mergeMap((result: IMatchComponentWithImage) => this.instrumentComponentSaved(result, workspaceId, dpId)));
  }

  private instrumentComponentSaved<T extends IMatchComponent>(
    component: T,
    workspaceId: string,
    digitalPropertyId: string,
  ): Observable<T> {
    return this.instrumentationApi
      .instrument(
        'Component saved',
        {
          componentId: component[$matchComponent._id],
          componentName: component[$matchComponent.name],
          workspaceId: workspaceId,
          digitalPropertyId: digitalPropertyId,
        },
        workspaceId,
        digitalPropertyId,
      )
      .pipe(
        map(() => component),
        catchError(() => of(component)),
      );
  }

  public archiveMatchComponent(matchComponentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId, digitalPropertyId }: ICurrentSelectedProperty): Observable<void> => {
        return this.matchComponentApi.archiveMatchComponent(workspaceId, digitalPropertyId, matchComponentId);
      }),
    );
  }

  public restoreMatchComponent(matchComponentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId, digitalPropertyId }: ICurrentSelectedProperty): Observable<void> => {
        return this.matchComponentApi.restoreMatchComponent(workspaceId, digitalPropertyId, matchComponentId);
      }),
    );
  }

  public getNameUniquenessValidator(
    workspaceId: string,
    digitalPropertyId: string,
    nameToSkip: () => string = (): string => '',
  ): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (SharedCommonUtility.isNullishOrEmpty(control.value?.trim()) || control.value === nameToSkip()) {
        return of(null);
      }

      return of(control.value).pipe(
        delay(DEBOUNCE_TIME),
        switchMap((name: string) =>
          this.matchComponentApi
            .exists(workspaceId, digitalPropertyId, { [$matchComponent.name]: name })
            .pipe(map((response: IExistsResponse) => (response.exists ? { unique: true } : null))),
        ),
      );
    };
  }

  public getCssSelectorUniquenessValidator(
    workspaceId: string,
    digitalPropertyId: string,
    selectorToSkip: () => string = (): string => '',
  ): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (SharedCommonUtility.isNullishOrEmpty(control.value?.trim()) || control.value === selectorToSkip()) {
        return of(null);
      }

      return of(control.value).pipe(
        delay(DEBOUNCE_TIME),
        switchMap((cssSelector: string) =>
          this.matchComponentApi
            .exists(workspaceId, digitalPropertyId, { [$matchComponent.cssSelector]: cssSelector })
            .pipe(map((response: IExistsResponse) => (response.exists ? { unique: true } : null))),
        ),
      );
    };
  }
}
